import { date, index, integer, pgTable, timestamp, uuid, varchar } from 'drizzle-orm/pg-core';
import { id } from './_shared';
import { users } from './users';

// =============================================================================
// CORPUS IMPORTS — the import-EVENT record (W3 remove-an-import). ONE row per
// import, through EITHER door: the paste door (POST /corpus/import) and the file
// door (POST /corpus/import-file). Before this table the paste door banked NO
// provenance at all (the file door already banks a source_documents blob row);
// this brings both doors to parity so a whole import can be UNDONE as a unit.
//
// The seeded roles/achievements still land in the SAME roles/achievements tables
// as always — they now carry a nullable `import_id` back-link (corpus.ts) to the
// row here that created them. A file import additionally sets source_document_id
// to the source_documents blob record it extracted from (a soft pointer, mirrors
// the source_ref jsonb pointer already on roles/achievements — traced, never
// generated).
//
// NEW table → CURRENT-LAW uuid shape from day one (CLAUDE.md 2026-06-22:
// "identity = per-user users.id uuid"), mirroring source_documents.ts — both a
// real users FK with ON DELETE CASCADE (a deleted user's import provenance goes
// with them).
//
// archivedAt is the IMPORT-LEVEL soft-remove marker (undo-this-import): NULL =
// live, a timestamp = archived. Reversible (cleared on unarchive). The linked
// roles/achievements carry their OWN archival mark — achievements.status =
// 'archived', roles.metadata.archived = true (the existing corpusMutations.ts
// idiom) — set in the same operation. NOTHING is ever hard-deleted.
// =============================================================================

export const corpusImports = pgTable(
  'corpus_imports',
  {
    id: id(),
    userId: uuid('user_id')
      .notNull()
      .references(() => users.id, { onDelete: 'cascade' }),
    /** Which door this import came through: 'paste' | 'file'. */
    kind: varchar('kind', { length: 16 }).notNull(),
    /** The declared source: 'paste' | 'linkedin' | 'cv' | 'resume' — the same
     *  vocabulary runCorpusImport already carries. */
    source: varchar('source', { length: 24 }).notNull(),
    /** The authoring date the import declared. Nullable — a paste may not
     *  declare one; NULL is the honest "not stated", never a guessed date. */
    documentDate: date('document_date'),
    /** File imports only: the source_documents blob record this import extracted
     *  from. NULL for a paste import (no blob). Soft pointer (no FK). */
    sourceDocumentId: uuid('source_document_id'),
    /** Honest counts of what the import produced (mirror the runCorpusImport
     *  response body). Never a fabricated number. */
    rolesCreated: integer('roles_created').notNull().default(0),
    rolesMatched: integer('roles_matched').notNull().default(0),
    achievementsCreated: integer('achievements_created').notNull().default(0),
    achievementsSkipped: integer('achievements_skipped').notNull().default(0),
    /** The import-level soft-remove marker (undo-this-import). NULL = live; a
     *  timestamp = archived. Reversible (cleared on unarchive). */
    archivedAt: timestamp('archived_at', { withTimezone: true }),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => ({
    userIdx: index('corpus_imports_user_idx').on(t.userId),
  }),
);
